import {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from "react";

export type ColorMode = "light" | "dark" | "system";
export type ResolvedColorMode = "light" | "dark";

// Preset color themes
export type ColorTheme =
  | "indigo"
  | "blue"
  | "green"
  | "orange"
  | "rose"
  | "slate";

export interface ThemeColors {
  primary: string;
  primaryForeground: string;
  ring: string;
}

// Theme color definitions for light and dark modes
export const themeColors: Record<
  ColorTheme,
  { light: ThemeColors; dark: ThemeColors }
> = {
  indigo: {
    light: {
      primary: "#6366f1",
      primaryForeground: "#ffffff",
      ring: "#6366f1",
    },
    dark: {
      primary: "#818cf8",
      primaryForeground: "#0a0a12",
      ring: "#818cf8",
    },
  },
  blue: {
    light: {
      primary: "#3b82f6",
      primaryForeground: "#ffffff",
      ring: "#3b82f6",
    },
    dark: {
      primary: "#60a5fa",
      primaryForeground: "#0a0a12",
      ring: "#60a5fa",
    },
  },
  green: {
    light: {
      primary: "#22c55e",
      primaryForeground: "#ffffff",
      ring: "#22c55e",
    },
    dark: {
      primary: "#4ade80",
      primaryForeground: "#0a0a12",
      ring: "#4ade80",
    },
  },
  orange: {
    light: {
      primary: "#f97316",
      primaryForeground: "#ffffff",
      ring: "#f97316",
    },
    dark: {
      primary: "#fb923c",
      primaryForeground: "#0a0a12",
      ring: "#fb923c",
    },
  },
  rose: {
    light: {
      primary: "#f43f5e",
      primaryForeground: "#ffffff",
      ring: "#f43f5e",
    },
    dark: {
      primary: "#fb7185",
      primaryForeground: "#0a0a12",
      ring: "#fb7185",
    },
  },
  slate: {
    light: {
      primary: "#475569",
      primaryForeground: "#ffffff",
      ring: "#475569",
    },
    dark: {
      primary: "#94a3b8",
      primaryForeground: "#0a0a12",
      ring: "#94a3b8",
    },
  },
};

export const themeLabels: Record<ColorTheme, string> = {
  indigo: "Indigo",
  blue: "Blue",
  green: "Green",
  orange: "Orange",
  rose: "Rose",
  slate: "Slate",
};

interface UseThemeOptions {
  /** Storage key for persisting color mode preference. Defaults to "color-mode" */
  colorModeStorageKey?: string;
  /** Storage key for persisting color theme preference. Defaults to "color-theme" */
  colorThemeStorageKey?: string;
  /** Default color mode when no preference is stored. Defaults to "system" */
  defaultColorMode?: ColorMode;
  /** Default color theme when no preference is stored. Defaults to "indigo" */
  defaultColorTheme?: ColorTheme;
}

interface UseThemeReturn {
  /** Current color mode setting (light, dark, or system) */
  colorMode: ColorMode;
  /** Resolved color mode based on system preference when mode is "system" */
  resolvedColorMode: ResolvedColorMode;
  /** Current color theme (indigo, blue, green, etc.) */
  colorTheme: ColorTheme;
  /** Set the color mode */
  setColorMode: (mode: ColorMode) => void;
  /** Set the color theme */
  setColorTheme: (theme: ColorTheme) => void;
  /** Toggle between light and dark (ignores system) */
  toggleColorMode: () => void;
  /** Whether the current resolved mode is dark */
  isDark: boolean;
  /** Available color themes */
  availableThemes: ColorTheme[];
}

function getSystemTheme(): ResolvedColorMode {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getStoredColorMode(storageKey: string): ColorMode | null {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem(storageKey);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return null;
}

function getStoredColorTheme(storageKey: string): ColorTheme | null {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem(storageKey);
  if (stored && stored in themeColors) {
    return stored as ColorTheme;
  }
  return null;
}

function applyThemeColors(theme: ColorTheme, isDark: boolean) {
  const root = document.documentElement;
  const colors = isDark ? themeColors[theme].dark : themeColors[theme].light;

  // Set the base variables (used by @theme to create --color-* variants)
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--primary-foreground", colors.primaryForeground);
  root.style.setProperty("--ring", colors.ring);
}

export function useTheme(options: UseThemeOptions = {}): UseThemeReturn {
  const {
    colorModeStorageKey = "color-mode",
    colorThemeStorageKey = "color-theme",
    defaultColorMode = "system",
    defaultColorTheme = "indigo",
  } = options;

  const [colorMode, setColorModeState] = useState<ColorMode>(() => {
    return getStoredColorMode(colorModeStorageKey) ?? defaultColorMode;
  });

  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
    return getStoredColorTheme(colorThemeStorageKey) ?? defaultColorTheme;
  });

  const [systemTheme, setSystemTheme] =
    useState<ResolvedColorMode>(getSystemTheme);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light");
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Calculate resolved color mode
  const resolvedColorMode: ResolvedColorMode =
    colorMode === "system" ? systemTheme : colorMode;

  const isDark = resolvedColorMode === "dark";

  // Apply dark/light class to document
  useEffect(() => {
    const root = document.documentElement;

    if (isDark) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [isDark]);

  // Apply theme colors
  useEffect(() => {
    applyThemeColors(colorTheme, isDark);
  }, [colorTheme, isDark]);

  // Persist color mode to localStorage
  useEffect(() => {
    localStorage.setItem(colorModeStorageKey, colorMode);
  }, [colorMode, colorModeStorageKey]);

  // Persist color theme to localStorage
  useEffect(() => {
    localStorage.setItem(colorThemeStorageKey, colorTheme);
  }, [colorTheme, colorThemeStorageKey]);

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode);
  }, []);

  const setColorTheme = useCallback((theme: ColorTheme) => {
    setColorThemeState(theme);
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorModeState((current) => {
      const currentResolved = current === "system" ? systemTheme : current;
      return currentResolved === "dark" ? "light" : "dark";
    });
  }, [systemTheme]);

  const availableThemes: ColorTheme[] = Object.keys(
    themeColors,
  ) as ColorTheme[];

  return {
    colorMode,
    resolvedColorMode,
    colorTheme,
    setColorMode,
    setColorTheme,
    toggleColorMode,
    isDark,
    availableThemes,
  };
}

// Context for sharing theme state across components
type ThemeContextValue = UseThemeReturn;

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useThemeContext(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }
  return context;
}

export { ThemeContext };
